import { Fragment } from 'react';
import type { ComponentType, Dispatch, SetStateAction } from 'react';
import { retryLazy } from '../../lib/retryLazy';
import type { Auftrag, Profile, PushNotifFn, ToastType } from '../../types';

// Die Ziel-Modals sind .tsx (typisiert seit TS-Migration 17.04.2026). Die
// Importe schreiben weiterhin `.jsx` — Vite löst das via ts-migration-resolve-
// Plugin (vite.config.js) zu `.tsx` auf. `ComponentType<any>` hier ist nur
// der Lazy-Wrapper-Type; echte Props-Validation passiert beim JSX-Aufruf,
// da die `.tsx`-Files ihren typisierten default-Export behalten.
// (Aktualisiert 22.05.2026)
const NeuerAuftragModal = retryLazy<ComponentType<any>>(() => import('../../modals/NeuerAuftragModal.jsx'));
const AngebotModal = retryLazy<ComponentType<any>>(() => import('../../modals/AngebotModal.jsx'));
const TransportauftragModal = retryLazy<ComponentType<any>>(() => import('../../modals/TransportauftragModal.jsx'));
const ChatModal = retryLazy<ComponentType<any>>(() => import('../../modals/ChatModal.jsx'));
const ChatInbox = retryLazy<ComponentType<any>>(() => import('../../modals/ChatInbox.jsx'));
const ConfirmModal = retryLazy<ComponentType<any>>(() => import('../../modals/ConfirmModal.jsx'));

/**
 * Modal-State-Shape (verwaltet in App.jsx).
 *
 * `type` ist der Modal-Identifier, `data` der Payload (variiert je nach Modal):
 *  - `'neuerAuftrag'` → Initial-Werte für Felder
 *  - `'angebot'` | `'transportauftrag'` | `'chat'` → `Auftrag`
 *  - `'confirm'` → Confirm-Dialog-Config
 *  - `'chatInbox'` → kein Payload
 */
export interface ModalState {
  type: string | null;
  data: unknown;
}


interface ModalRendererProps {
  modal: ModalState;
  setModal: Dispatch<SetStateAction<ModalState>>;
  user: Profile | null | undefined;
  users: Profile[];
  setAuftraege: Dispatch<SetStateAction<Auftrag[]>>;
  openToast: (msg: string, type?: ToastType) => void;
  pushNotif: PushNotifFn;
  ladeChatUngelesen?: () => void;
  openModal: (type: string, data?: unknown) => void;
}

/**
 * ModalRenderer — zentraler Renderer für alle global verwalteten Modals.
 *
 * Alle Modals werden lazy-geladen (`retryLazy`) und nur gemountet, wenn sie
 * aktiv sind. Nicht alle Modals akzeptieren ein `open`-Prop — einige werden
 * conditionally gemountet (z.B. `TransportauftragModal`), andere über ein
 * `open={modal.type === 'xyz'}` gesteuert (z.B. `NeuerAuftragModal`).
 */
export function ModalRenderer({
  modal,
  setModal,
  user,
  users,
  setAuftraege,
  openToast,
  pushNotif,
  ladeChatUngelesen,
  openModal,
}: ModalRendererProps) {
  const onClose = (): void => {
    setModal({ type: null, data: null });
  };

  return (
    <Fragment>
      <NeuerAuftragModal
        open={modal.type === 'neuerAuftrag'}
        onClose={onClose}
        user={user}
        users={users}
        setAuftraege={setAuftraege}
        openToast={openToast}
        pushNotif={pushNotif}
        initialData={modal.data}
        onOpenModal={openModal} />
      <AngebotModal
        open={modal.type === 'angebot'}
        onClose={onClose}
        auftrag={modal.data}
        user={user}
        setAuftraege={setAuftraege}
        openToast={openToast}
        pushNotif={pushNotif} />
      {modal.type === 'transportauftrag' &&
        <TransportauftragModal auftrag={modal.data} users={users} onClose={onClose} />}
      {modal.type === 'chat' &&
        <ChatModal
          open={true}
          onClose={function () {
            onClose();
            if (ladeChatUngelesen) ladeChatUngelesen();
          }}
          auftrag={modal.data}
          user={user}
          users={users}
          pushNotif={pushNotif}
          openToast={openToast} />}
      {modal.type === 'confirm' &&
        <ConfirmModal open={true} onClose={onClose} data={modal.data} />}
      {modal.type === 'chatInbox' &&
        <ChatInbox
          open={true}
          onClose={onClose}
          onOpenChat={function (auftrag: Auftrag) {
            openModal('chat', auftrag);
          }} />}
    </Fragment>
  );
}

export default ModalRenderer;
